import apiClient, { getAuthMode, getAuthToken } from './client';

export type ChatMessageRequest = {
  query: string;
  conversation_id?: number | null;
};

export type ChatMetadata = {
  rows_analyzed?: number;
  domains_queried?: string[];
  processing_time_ms?: number;
};

export type TableArtifactData =
  | {
      columns: string[];
      rows: unknown[][];
    }
  | Record<string, unknown>;

/** Key–value metrics for summary cards (e.g. counts, totals). */
export type MetricArtifactData = Record<string, unknown>;

export type ChatArtifact =
  | {
      type: 'table';
      title?: string;
      data: TableArtifactData;
      footnotes?: string[];
    }
  | {
      type: 'metric';
      title?: string;
      data: MetricArtifactData;
      footnotes?: string[];
    };

/**
 * Wrapper returned on transcript messages (`GET …/conversations/{id}/messages`).
 * Renderable fields live on the nested `artifact` object; the outer row adds DB metadata.
 */
export type PersistedAgentRunArtifactOut = {
  artifact_type?: string;
  title?: string | null;
  artifact?: {
    type?: unknown;
    title?: unknown;
    data?: unknown;
    footnotes?: unknown;
  };
};

/** Map persisted transcript artifact → the same `ChatArtifact` shape used after `POST /message`. */
export function normalizePersistedMessageArtifact(
  wrapper: PersistedAgentRunArtifactOut | null | undefined,
): ChatArtifact | undefined {
  if (!wrapper || typeof wrapper !== 'object') return undefined;
  const inner = wrapper.artifact;
  if (!inner || typeof inner !== 'object') return undefined;

  const typeRaw =
    (typeof inner.type === 'string' && inner.type) ||
    (typeof wrapper.artifact_type === 'string' && wrapper.artifact_type) ||
    '';

  const titleFromInner = typeof inner.title === 'string' ? inner.title : undefined;
  const titleFromWrapper =
    typeof wrapper.title === 'string' && wrapper.title.trim() !== ''
      ? wrapper.title
      : undefined;
  const title = titleFromInner ?? titleFromWrapper;

  const footnotes = Array.isArray(inner.footnotes)
    ? inner.footnotes.filter((f): f is string => typeof f === 'string')
    : undefined;

  if (
    typeRaw === 'table' &&
    inner.data &&
    typeof inner.data === 'object' &&
    !Array.isArray(inner.data)
  ) {
    return {
      type: 'table',
      ...(title !== undefined ? { title } : {}),
      data: inner.data as TableArtifactData,
      ...(footnotes?.length ? { footnotes } : {}),
    };
  }

  if (
    typeRaw === 'metric' &&
    inner.data &&
    typeof inner.data === 'object' &&
    !Array.isArray(inner.data)
  ) {
    return {
      type: 'metric',
      ...(title !== undefined ? { title } : {}),
      data: inner.data as MetricArtifactData,
      ...(footnotes?.length ? { footnotes } : {}),
    };
  }

  return undefined;
}

export type ChatMessageResponse = {
  conversation_id: number;
  message_id: number;
  response: string;
  artifact?: ChatArtifact;
  follow_up_suggestions?: string[];
  permission_denied?: boolean;
  denied_modules?: string[];
  sources?: string[];
  metadata?: ChatMetadata;
};

type AgentStreamEvent = {
  type?: string;
  [key: string]: unknown;
};

type AgentStreamHandlers = {
  onPartialAnswer?: (delta: string) => void;
  onEvent?: (event: AgentStreamEvent) => void;
};

export type ConversationSummary = {
  id: number;
  title: string | null;
  created_at: string;
  updated_at: string | null;
  message_count: number;
  last_message_at: string | null;
};

export type ConversationListResponse = {
  conversations: ConversationSummary[];
};

export type MessageOut = {
  id: number;
  role: string;
  content: string;
  domains_queried?: string[] | null;
  denied_modules?: string[] | null;
  permission_denied?: boolean;
  processing_time_ms?: number | null;
  created_at: string;
  artifact?: PersistedAgentRunArtifactOut | null;
};

/** Response from `GET /api/v1/agent/conversations/{id}/messages` (shape may include optional conversation fields). */
export type ConversationDetailResponse = {
  messages: MessageOut[];
  id?: number;
  title?: string | null;
};

/**
 * Axios allows a relative `baseURL` (e.g. `/api`), but `new URL(path, base)` requires an absolute base.
 */
function resolveAbsoluteApiBase(): string {
  const raw = String(apiClient.defaults.baseURL ?? '').trim();
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'http://localhost';
  if (!raw) return origin;
  if (/^https?:\/\//i.test(raw)) {
    return raw.replace(/\/+$/, '') || origin;
  }
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `${origin}${path}`.replace(/\/+$/, '') || origin;
}

export async function postChatMessage(
  body: ChatMessageRequest,
  handlers?: AgentStreamHandlers,
): Promise<ChatMessageResponse> {
  const endpoint = new URL(
    'api/v1/agent/message',
    `${resolveAbsoluteApiBase()}/`,
  ).toString();
  const authMode = getAuthMode();
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/x-ndjson, application/json, text/plain',
  };

  if (authMode === 'bearer' && token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    credentials: authMode === 'cookie' ? 'include' : 'same-origin',
    body: JSON.stringify({
      query: body.query,
      conversation_id: body.conversation_id ?? null,
      stream: true,
    }),
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(errText || `Request failed with status ${response.status}`);
  }

  // NDJSON streams are often mislabeled as application/json; never consume the
  // whole body with response.json() here or streaming will never paint.
  const reader = response.body?.getReader();
  if (!reader) {
    const data = (await response.json()) as ChatMessageResponse;
    return data;
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let stitchedAnswer = '';
  let finalAnswer = '';
  let artifact: ChatArtifact | undefined;
  let followUpSuggestions: string[] | undefined;
  let conversationId = body.conversation_id ?? null;
  let deniedModules: string[] | undefined;
  let permissionDenied = false;
  let streamError: string | null = null;
  // partial_answer often streams a full JSON object as text; extract only narrative text live.
  let narrativeStarted = false;
  let narrativeDone = false;
  let narrativeScanIndex = -1;
  let narrativeEscaped = false;
  let narrativeUnicodeBuffer = '';
  let narrativeUnicodePending = false;

  const decodeEscapedChar = (ch: string): string => {
    switch (ch) {
      case 'b':
        return '\b';
      case 'f':
        return '\f';
      case 'n':
        return '\n';
      case 'r':
        return '\r';
      case 't':
        return '\t';
      case '"':
      case '\\':
      case '/':
        return ch;
      default:
        return ch;
    }
  };

  const extractNarrativeDelta = (): string => {
    if (narrativeDone) return '';

    if (!narrativeStarted) {
      const keyIdx = stitchedAnswer.indexOf('"narrative"');
      if (keyIdx === -1) return '';
      const colonIdx = stitchedAnswer.indexOf(':', keyIdx);
      if (colonIdx === -1) return '';
      const quoteIdx = stitchedAnswer.indexOf('"', colonIdx);
      if (quoteIdx === -1) return '';
      narrativeStarted = true;
      narrativeScanIndex = quoteIdx + 1;
    }

    if (narrativeScanIndex < 0 || narrativeScanIndex >= stitchedAnswer.length) {
      return '';
    }

    let out = '';
    let i = narrativeScanIndex;
    while (i < stitchedAnswer.length) {
      const ch = stitchedAnswer[i];

      if (narrativeUnicodePending) {
        if (/^[0-9a-fA-F]$/.test(ch)) {
          narrativeUnicodeBuffer += ch;
          i += 1;
          if (narrativeUnicodeBuffer.length === 4) {
            out += String.fromCharCode(parseInt(narrativeUnicodeBuffer, 16));
            narrativeUnicodeBuffer = '';
            narrativeUnicodePending = false;
          }
          continue;
        }
        // malformed/incomplete unicode escape; wait for more stream content
        break;
      }

      if (narrativeEscaped) {
        if (ch === 'u') {
          narrativeUnicodePending = true;
          narrativeUnicodeBuffer = '';
          narrativeEscaped = false;
          i += 1;
          continue;
        }
        out += decodeEscapedChar(ch);
        narrativeEscaped = false;
        i += 1;
        continue;
      }

      if (ch === '\\') {
        narrativeEscaped = true;
        i += 1;
        continue;
      }

      if (ch === '"') {
        narrativeDone = true;
        i += 1;
        break;
      }

      out += ch;
      i += 1;
    }

    narrativeScanIndex = i;
    return out;
  };

  /** Lets the browser paint between stream events (avoids one giant sync batch). */
  const waitNextFrame = () =>
    new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve());
    });

  /** How often to yield to the main thread while many partial_answer lines arrive in one chunk. */
  const PARTIAL_YIELD_EVERY = 4;
  let partialAnswerCount = 0;

  const processEvent = (event: AgentStreamEvent): boolean => {
    handlers?.onEvent?.(event);
    if (Array.isArray(event.denied_modules)) {
      deniedModules = event.denied_modules.filter(
        (m): m is string => typeof m === 'string',
      );
      if (deniedModules.length > 0) permissionDenied = true;
    }

    if (event.type === 'run_started' || event.type === 'run_complete') {
      const cid = event.conversation_id;
      if (typeof cid === 'number') conversationId = cid;
      return false;
    }

    if (event.type === 'partial_answer') {
      const delta = event.delta;
      if (typeof delta === 'string') {
        stitchedAnswer += delta;
        const narrativeDelta = extractNarrativeDelta();
        if (narrativeDelta) {
          handlers?.onPartialAnswer?.(narrativeDelta);
        }
        partialAnswerCount += 1;
        // First token + every Nth: yield so the UI can paint (sync bursts coalesce otherwise).
        return (
          partialAnswerCount === 1 ||
          partialAnswerCount % PARTIAL_YIELD_EVERY === 0
        );
      }
      return false;
    }

    if (event.type === 'error') {
      const message = event.message;
      streamError =
        typeof message === 'string' && message.trim().length > 0
          ? message
          : 'Streaming failed.';
      return false;
    }

    if (event.type === 'final') {
      const answer = event.answer as
        | {
            narrative?: string;
            permission_notes?: unknown[];
            artifact?: unknown;
            follow_up_suggestions?: unknown[];
          }
        | undefined;
      if (answer?.narrative && typeof answer.narrative === 'string') {
        finalAnswer = answer.narrative;
      }

      const maybeArtifact = answer?.artifact as
        | {
            type?: unknown;
            title?: unknown;
            data?: unknown;
            footnotes?: unknown;
          }
        | undefined;
      if (
        maybeArtifact?.type === 'table' &&
        maybeArtifact.data &&
        typeof maybeArtifact.data === 'object'
      ) {
        artifact = {
          type: 'table',
          title:
            typeof maybeArtifact.title === 'string'
              ? maybeArtifact.title
              : undefined,
          data: maybeArtifact.data as TableArtifactData,
          footnotes: Array.isArray(maybeArtifact.footnotes)
            ? maybeArtifact.footnotes.filter(
                (f): f is string => typeof f === 'string',
              )
            : undefined,
        };
      } else if (
        maybeArtifact?.type === 'metric' &&
        maybeArtifact.data &&
        typeof maybeArtifact.data === 'object' &&
        !Array.isArray(maybeArtifact.data)
      ) {
        artifact = {
          type: 'metric',
          title:
            typeof maybeArtifact.title === 'string'
              ? maybeArtifact.title
              : undefined,
          data: maybeArtifact.data as MetricArtifactData,
          footnotes: Array.isArray(maybeArtifact.footnotes)
            ? maybeArtifact.footnotes.filter(
                (f): f is string => typeof f === 'string',
              )
            : undefined,
        };
      }
      const followUps = answer?.follow_up_suggestions;
      if (Array.isArray(followUps)) {
        followUpSuggestions = followUps.filter(
          (item): item is string =>
            typeof item === 'string' && item.trim().length > 0,
        );
      }

      const notes = answer?.permission_notes;
      if (Array.isArray(notes) && notes.length > 0) {
        permissionDenied = true;
      }
    }
    return false;
  };

  const processBuffer = async (flush = false) => {
    const lines = buffer.split(/\r?\n/);
    if (!flush) buffer = lines.pop() ?? '';
    else buffer = '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        const parsed = JSON.parse(trimmed) as AgentStreamEvent;
        const needsPaintYield = processEvent(parsed);
        if (needsPaintYield) await waitNextFrame();
      } catch {
        // Ignore malformed stream chunks.
      }
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    await processBuffer(false);
  }

  buffer += decoder.decode();
  await processBuffer(true);

  if (streamError) {
    throw new Error(streamError);
  }

  const responseText = finalAnswer || stitchedAnswer || 'No response received.';

  return {
    conversation_id: conversationId ?? 0,
    message_id: Date.now(),
    response: responseText,
    artifact,
    follow_up_suggestions: followUpSuggestions,
    permission_denied: permissionDenied,
    denied_modules: deniedModules,
  };
}

function sortConversationsNewestFirst(list: ConversationSummary[]): ConversationSummary[] {
  return [...list].sort((a, b) => {
    const ta = Date.parse(a.last_message_at ?? a.updated_at ?? a.created_at) || 0;
    const tb = Date.parse(b.last_message_at ?? b.updated_at ?? b.created_at) || 0;
    if (tb !== ta) return tb - ta;
    return b.id - a.id;
  });
}

/** List conversations (no pagination). Messages are loaded per conversation via `fetchConversationMessages`. */
export async function fetchConversations(): Promise<ConversationSummary[]> {
  const { data } = await apiClient.get<ConversationListResponse>('/api/v1/agent/conversations');
  const raw = data.conversations ?? [];
  return sortConversationsNewestFirst(raw);
}

export async function fetchConversationMessages(
  conversationId: number,
): Promise<ConversationDetailResponse> {
  const { data } = await apiClient.get<ConversationDetailResponse>(
    `/api/v1/agent/conversations/${conversationId}/messages`,
  );
  return data;
}
